import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useLocation } from "../hooks/useLocations";
import { BASE_KEYWORDS, PAGE_KEYWORDS, SITE_URL, TACOVERSE_MENU_URL, TACOVERSE_ORDER_URL, breadcrumbSchema } from "../data/brand";

const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

export default function LocationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: l, isLoading, isError } = useLocation(slug);

  useEffect(() => {
    if (isError) navigate("/locations", { replace: true });
  }, [isError, navigate]);

  if (isLoading || !l) {
    return (
      <div className="container-x min-h-[60vh] pt-32" data-testid="location-detail-loading">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-bv-sub">Loading location…</p>
      </div>
    );
  }

  const openingSoon = l.status === "opening_soon";

  const schemaBar = {
    "@context": "https://schema.org",
    "@type": "Bar",
    name: `Barverse — ${l.city}`,
    servesCuisine: ["Tex-Mex", "Bar"],
    url: `${SITE_URL}/locations/${l.slug}`,
  };
  if (l.phone) schemaBar.telephone = l.phone;
  if (l.address) {
    schemaBar.address = {
      "@type": "PostalAddress",
      streetAddress: l.address.line1,
      addressLocality: l.address.city,
      addressRegion: l.address.state,
      postalCode: l.address.zip,
      addressCountry: "US",
    };
  }

  const jsonLd = [schemaBar, breadcrumbSchema(`/locations/${l.slug}`, `Barverse — ${l.city}`)];

  return (
    <div className="pt-28 md:pt-36" data-testid="location-detail">
      <Seo
        title={`Barverse — ${l.city}`}
        description={
          openingSoon
            ? `Barverse is opening soon in ${l.city} — inside Tacoverse. Address, phone and bar hours announced at opening. Plan your group event now.`
            : `Barverse — ${l.city}, inside Tacoverse. Address, bar hours, phone, directions, drinks and the full Tacoverse food menu.`
        }
        path={`/locations/${l.slug}`}
        jsonLd={jsonLd}
        keywords={`${BASE_KEYWORDS}, ${PAGE_KEYWORDS.locations}`}
      />

      <nav className="container-x" aria-label="Breadcrumb" data-testid="location-breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-bv-faint">
          <li><Link to="/" className="transition-colors hover:text-bv-ink">Home</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/locations" className="transition-colors hover:text-bv-ink">Locations</Link></li>
          <li aria-hidden="true">/</li>
          <li className="text-bv-sub">{l.city}</li>
        </ol>
      </nav>

      <header className="container-x mt-8">
        <Reveal>
          <div className="flex flex-wrap items-center gap-4">
            <p className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#B45309]">
              <img src="/images/tacosun.png" alt="" className="h-4 w-auto" aria-hidden="true" />
              Inside Tacoverse™
            </p>
            {openingSoon && (
              <span className="chip !border-bv-flame/60 !text-bv-flame" data-testid="location-opening-soon">
                Opening Soon
              </span>
            )}
          </div>
          <h1 className="mt-4 font-display text-5xl uppercase leading-[0.9] text-bv-ink sm:text-6xl lg:text-7xl">
            Barverse — {l.city}
          </h1>
          {l.region && <p className="mt-4 text-lg text-bv-sub">{l.region}</p>}
          {openingSoon && l.opening_note && (
            <p
              className="mt-6 max-w-2xl rounded-xl border border-bv-flame/40 bg-bv-panel px-5 py-4 text-sm leading-relaxed text-bv-ink"
              data-testid="location-opening-note"
            >
              {l.opening_note}
            </p>
          )}
        </Reveal>
      </header>

      <div className="container-x mt-12 grid gap-6 pb-24 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <Reveal>
            <div className="rounded-2xl border border-bv-line bg-bv-panel p-8" data-testid="location-address">
              <p className="field-label">Address</p>
              {l.address ? (
                <p className="flex items-start gap-3 text-bv-ink">
                  <MapPin className="mt-1 h-4 w-4 shrink-0 text-bv-flame" aria-hidden="true" />
                  <span>
                    {l.address.line1}
                    {l.address.line2 && <>, {l.address.line2}<br /></>}
                    {!l.address.line2 && <br />}
                    {l.address.city}, {l.address.state} {l.address.zip}
                  </span>
                </p>
              ) : (
                <p className="flex items-start gap-3 text-bv-sub">
                  <MapPin className="mt-1 h-4 w-4 shrink-0 text-bv-flame" aria-hidden="true" />
                  <span>Exact address announced at opening.</span>
                </p>
              )}
              {l.phone ? (
                <a
                  href={`tel:${l.phone.replace(/[^+\d]/g, "")}`}
                  className="mt-5 flex items-center gap-3 font-semibold text-bv-ink transition-colors hover:text-bv-flame"
                  data-testid="location-detail-phone"
                >
                  <Phone className="h-4 w-4 text-bv-flame" aria-hidden="true" />
                  {l.phone}
                </a>
              ) : (
                <p className="mt-5 flex items-center gap-3 text-sm text-bv-sub">
                  <Phone className="h-4 w-4 shrink-0 text-bv-flame" aria-hidden="true" />
                  Phone announced at opening.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.06}>
            <div className="rounded-2xl border border-bv-line bg-bv-panel p-8" data-testid="location-hours">
              <p className="field-label">Barverse hours</p>
              {l.hours && l.hours.length > 0 ? (
                <>
                  <ul className="grid gap-2 font-mono text-sm">
                    {l.hours.map((h) => (
                      <li key={h.days} className="flex justify-between gap-4 border-b border-bv-line/60 pb-2">
                        <span className="text-bv-sub">{h.days}</span>
                        <span className="text-bv-ink">{h.open} – {h.close}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs leading-relaxed text-bv-faint">
                    Barverse keeps its own hours — they may differ from Tacoverse restaurant hours.
                  </p>
                </>
              ) : (
                <p className="text-sm leading-relaxed text-bv-sub">
                  Barverse hours will be announced at opening — Barverse keeps its own hours, which
                  may differ from Tacoverse restaurant hours.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-bv-line bg-bv-panel p-8" data-testid="location-sports">
              <p className="field-label">Sports & TVs</p>
              <p className="text-sm leading-relaxed text-bv-sub">{l.sports_note}</p>
            </div>
          </Reveal>
        </div>

        <aside className="lg:col-span-5">
          <Reveal delay={0.08}>
            <div className="rounded-2xl border border-bv-flame/40 bg-bv-flame/5 p-8">
              <p className="field-label">{openingSoon ? "Before the doors open" : "At this Barverse"}</p>
              <div className="grid gap-3">
                {l.maps_query && (
                  <a
                    href={mapsUrl(l.maps_query)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full"
                    data-testid="location-detail-directions"
                  >
                    One-tap directions <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                )}
                <Link to="/drinks" className="btn-tile" data-testid="location-detail-drinks">Drink Menu</Link>
                <Link to="/food" className="btn-tile" data-testid="location-detail-food">Tacoverse Food Menu</Link>
                <a href={TACOVERSE_MENU_URL} className="btn-tile" data-testid="location-detail-tacoverse-menu">
                  Full Tacoverse menu online <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <a href={TACOVERSE_ORDER_URL} className="btn-tile" data-testid="location-detail-order">
                  Order Online <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <Link
                  to={`/groups?location=${l.slug}`}
                  className="btn-ghost w-full"
                  data-testid="location-detail-groups"
                >
                  Group Inquiry
                </Link>
              </div>
              {openingSoon && (
                <p className="mt-5 text-sm leading-relaxed text-bv-sub">
                  Planning something for opening season? Send an inquiry and our team will follow
                  up as opening approaches.
                </p>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <Link
              to="/locations"
              className="mt-6 inline-block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-bv-sub transition-colors hover:text-bv-ink"
              data-testid="location-detail-back"
            >
              ← All Barverse locations
            </Link>
          </Reveal>
        </aside>
      </div>
    </div>
  );
}
